import type {
  AbstractPartDefinition,
  DragConstraint,
  DragUpdateOptions,
} from "@project/emerge-types";
import { Canvas } from "@react-three/fiber";
import {
  createElement,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Group, Vector3 } from "three";
import type { SceneDefinition } from "./Manufacturer";
import { useManufacturer } from "./ManufacturerProvider";
import { MissingPartDisplay } from "./MissingPartDisplay";
import { ProductFloorAligner } from "./ProductFloorAligner";
import { useProductStoreContext } from "./ProductStoreContext";
import { ProductViewerCamera } from "./ProductViewerCamera";
import { useProductViewerConfig } from "./ProductViewerConfigContext";
import { ProductViewerLoadingOverlay } from "./ProductViewerLoadingOverlay";
import {
  BlueprintRendererComponent,
  PartRendererComponent,
} from "./ProductViewerPartTree";
import { ProductViewerStage } from "./ProductViewerStage";
import {
  InteractiveGroup,
  useProductViewerGroupInteractions,
} from "./useProductViewerGroupInteractions";

/**
 * Renders the measurement overlay and subscribes to only the measurement
 * visibility slice. Isolating it here means showing/hiding measurements
 * re-renders this small component instead of the whole product viewer and its
 * part tree (which is what previously caused a full re-render on first enable).
 */
const MeasurementOverlay = ({
  target,
}: {
  target: RefObject<Group | null>;
}) => {
  const manufacturer = useManufacturer();
  const useProductStore = useProductStoreContext();
  const visible = useProductStore(
    (state) => !state.hiddenPartTypes.includes("Measurements"),
  );

  if (!manufacturer.measurementRenderer) {
    return null;
  }

  return createElement(manufacturer.measurementRenderer, {
    target,
    visible,
    key: "measurement",
  });
};

export const ProductViewerXR = () => {
  const manufacturer = useManufacturer();
  const { loadingScreen } = useProductViewerConfig();
  const useProductStore = useProductStoreContext();
  // Subscribe with selectors (not the whole store) so visibility toggles —
  // which only change `hiddenPartTypes` — do not re-render the whole viewer
  // and its part tree. Visibility is consumed by the individual renderers.
  const selectedSceneId = useProductStore((state) => state.selectedSceneId);
  const updateUserOptions = useProductStore((state) => state.updateUserOptions);
  const buildVersion = useProductStore((state) => state.buildVersion);
  const product = useProductStore((state) => state.product);
  const productGroupRef = useRef<Group>(null);
  const [isInitialViewReady, setIsInitialViewReady] = useState(false);
  const [initialFitToken, setInitialFitToken] = useState(0);
  const isLoadingOverlayEnabled = loadingScreen !== false;
  const isCanvasVisible = !isLoadingOverlayEnabled || isInitialViewReady;

  useEffect(() => {
    if (!product) return;
    setIsInitialViewReady(false);
    setInitialFitToken((token) => token + 1);
  }, [product]);

  const handleInitialFitComplete = useCallback(() => {
    setIsInitialViewReady(true);
  }, []);

  const selectedScene = useMemo(() => {
    if (!manufacturer.scenes) {
      return undefined;
    }

    const sceneIdToFind = selectedSceneId ?? "default";
    return manufacturer.scenes.find(
      (scene: SceneDefinition) => scene.id === sceneIdToFind,
    );
  }, [selectedSceneId, manufacturer.scenes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: buildVersion is intentionally included to trigger recomputation after incremental builds
  const partsByGroup = useMemo(() => {
    if (!product?.partDefinitions) return {};
    const partsArray = Array.from(product.partDefinitions.values());
    return partsArray.reduce(
      (acc, part) => {
        const group = part.group ?? "default";
        acc[group] ??= [];
        acc[group].push(part);
        return acc;
      },
      {} as Record<string, AbstractPartDefinition<string>[]>,
    );
  }, [product, product?.partDefinitions, buildVersion]);

  const { groupStates, toggleGroup, registerGroup } =
    useProductViewerGroupInteractions(product);

  // Drag rebuild throttle: pointermove can fire several times per frame, and
  // each move would otherwise kick a full product.build(). We coalesce drag
  // updates to at most one build per animation frame. The store's own coalesce
  // logic then guarantees only one build in-flight plus one pending, and the
  // final onDragEnd update is never dropped (it overwrites the pending patch
  // and flushes on the next frame -> guaranteed trailing build).
  const dragBuildRafRef = useRef<number | null>(null);
  const pendingDragSettingsRef = useRef<object | null>(null);

  const flushDragBuild = useCallback(() => {
    dragBuildRafRef.current = null;
    const settings = pendingDragSettingsRef.current;
    pendingDragSettingsRef.current = null;
    if (settings) {
      void updateUserOptions(settings, { coalesce: true });
    }
  }, [updateUserOptions]);

  useEffect(() => {
    return () => {
      if (dragBuildRafRef.current !== null) {
        cancelAnimationFrame(dragBuildRafRef.current);
      }
    };
  }, []);

  const handlePartDragUpdate = useCallback(
    (
      partId: string,
      newPosition: Vector3,
      constraint: DragConstraint,
      settingsContext: Record<string, unknown> | undefined,
      options?: DragUpdateOptions,
    ) => {
      if (!product) return;

      const result = product.handleDragUpdate(
        partId,
        newPosition,
        constraint,
        settingsContext,
        options,
      );

      if (result) {
        // Keep only the latest settings patch (absolute, not a delta) and run
        // the build once per frame instead of once per pointermove.
        pendingDragSettingsRef.current = result.settingsUpdate;
        dragBuildRafRef.current ??= requestAnimationFrame(flushDragBuild);
        return {
          displayValues: result.displayValues,
          snappedPosition: result.snappedPosition,
        };
      }
    },
    [product, flushDragBuild],
  );

  if (!product) return null;

  return (
    <div
      className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
      style={{
        flex: "1 1 0%",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
    >
      <Canvas
        className={`h-full w-full transition-opacity duration-300 ${
          isCanvasVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          height: "100%",
          opacity: isCanvasVisible ? 1 : 0,
          transition: "opacity 300ms ease",
          width: "100%",
        }}
        camera={{ position: [0.75, 1.125, 1.875], fov: 50, near: 0.01 }}
        gl={{ antialias: true }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0);
        }}
      >
        <ProductViewerStage scene={selectedScene} />
        <ProductViewerCamera
          key={initialFitToken}
          onInitialFitComplete={handleInitialFitComplete}
        >
          <ProductFloorAligner>
            <group ref={productGroupRef}>
              {product.blueprint &&
              manufacturer.blueprintRenderers[product.blueprint.type] ? (
                <BlueprintRendererComponent
                  blueprint={product.blueprint}
                  renderer={
                    manufacturer.blueprintRenderers[product.blueprint.type]
                  }
                />
              ) : null}
              <MeasurementOverlay target={productGroupRef} />
              {Object.entries(partsByGroup).map(([group, parts]) => {
                return (
                  <InteractiveGroup
                    key={`group-${group}`}
                    groupName={group}
                    groupState={groupStates[group]}
                    onToggle={toggleGroup}
                    onRegister={registerGroup}
                  >
                    {parts.map((part) => {
                      const partRenderer =
                        manufacturer.partRenderers[part.type];

                      if (partRenderer) {
                        return (
                          <PartRendererComponent
                            key={`part-${part.id || part.type}-${group}`}
                            part={part}
                            renderer={partRenderer}
                            group={group}
                            onDragUpdate={handlePartDragUpdate}
                          />
                        );
                      }
                      return (
                        <MissingPartDisplay
                          key={`missing-${part.id || part.type}-${group}`}
                        />
                      );
                    })}
                  </InteractiveGroup>
                );
              })}
            </group>
          </ProductFloorAligner>
        </ProductViewerCamera>
      </Canvas>
      {isLoadingOverlayEnabled ? (
        <ProductViewerLoadingOverlay visible={!isInitialViewReady}>
          {loadingScreen}
        </ProductViewerLoadingOverlay>
      ) : null}
    </div>
  );
};
